import { getCarouselBulletHTML } from './templateUtils';
import { webkitLineClamp } from 'webkit-line-clamp';

export const viewPorts = {
  mobile: 360,
  tablet: 759.99,
  desktop: 1025,
  wide: 1450,
};

let currentTabElement = '';
let previousTabElement = '';
const outlineClass = 'swa-focus-outline';

let isAutoPlayInit = false;
let carouselAutoplayValue = 0;

// ------ CUSTOM LAZYLOAD
// Lazyloading implementation for elements that are not supported by Lazy Sizes
// Currently supports videos only
export const customLazyload = element => {
  if (!element || element.classList.contains('swa-lazyloaded')) {
    return;
  }

  for (const source in element.children) {
    if (element.children.hasOwnProperty(source)) {
      const elementSource = element.children[source];

      if ((typeof elementSource.tagName === 'string') && (elementSource.tagName === 'SOURCE')) {
        switch (element.tagName) {
        case 'VIDEO':
          if (!elementSource.dataset.src) {
            return;
          }
          elementSource.src = elementSource.dataset.src;

          const posterElement = element.parentElement.querySelector(
            'img[data-poster="true"]'
          );
          if (
            posterElement &&
            posterElement.dataset &&
            posterElement.dataset.src
          ) {
            posterElement.src = posterElement.dataset.src;
          }
          break;
        default:
          break;
        }
      }
    }
  }

  element.classList.remove('swa-lazyload');
  element.classList.add('swa-lazyloaded');
  if (element.tagName === 'VIDEO') {
    element.load();
  }
};

// ------ CAROUSELS
export const renderCarouselPagination = (
  currentSlide,
  totalSlides,
  targetElement
) => {
  if (!targetElement || !currentSlide || !totalSlides) {
    return;
  }

  let result = '';

  const formatPaginationNumber = number => {
    return number > 9 ? number : `0${number}`;
  };

  if (totalSlides) {
    const current = formatPaginationNumber(currentSlide);
    const total = formatPaginationNumber(totalSlides);
    result = `${current}/${total}`;
  }

  targetElement.innerHTML = result;
};

export const renderCarouselBullets = (
  carouselBulletsElement,
  carouselSlidesElement
) => {
  if (
    !carouselBulletsElement ||
    !carouselSlidesElement ||
    !carouselSlidesElement.children
  ) {
    return;
  }

  // carouselSlidesElement.children is an HTMLCollection which implements the iterable protocol
  [...carouselSlidesElement.children].forEach((slide, index) => {
    if (slide.classList.contains('glide__slide--clone')) {
      return;
    }

    const alt = slide.querySelector('img') ?
      slide.querySelector('img').alt :
      '';

    const data = {
      index,
      alt,
    };

    carouselBulletsElement.innerHTML += getCarouselBulletHTML(data);
  });
};

export const updateCarouselBulletsPressed = carouselElement => {
  if (!carouselElement) {
    return;
  }

  const carouselBulletsElement = carouselElement.querySelector(
    '.glide__bullets, .splide__pagination'
  );

  const activeBullet = carouselBulletsElement.querySelector(
    '.glide__bullet--active, .is-active'
  );

  const regularBullets = carouselBulletsElement.querySelectorAll(
    ':not(.glide__bullet--active):not(.is-active)'
  );

  if (regularBullets) {
    regularBullets.forEach(bullet => {
      bullet.setAttribute('aria-pressed', false);
    });
  }

  if (activeBullet) {
    activeBullet.setAttribute('aria-pressed', true);
  } else {
    if (regularBullets.length) {
      regularBullets[0].setAttribute('aria-pressed', true);
    }
  }
};

export const getActiveCarouselSlide = carouselElement => {
  let active = carouselElement.querySelector('.glide__slide--active, .swa-splide-teaser-carousel__item.is-visible');
  if (!active) {
    active = carouselElement.querySelector('.glide__slide, .splide__slide');
  }

  return active;
};

export const controlCarouselLazyload = (
  carouselElement,
  carouselInstance,
  onRun
) => {
  if (!carouselElement) {
    return;
  }

  // Check if the carousel is an instance of Splide
  const isSplide = document.querySelector('.js-swa-splide-teaser-carousel') !== null;

  // Check the content type (image or video) and return it,
  // together with the  img/video element
  const validateSlideContent = slide => {
    if (!slide) {
      return;
    }

    const imageSlideContent = slide.querySelector('img');
    if (imageSlideContent) {
      if (!imageSlideContent.dataset.poster) {
        return {
          type: 'image',
          element: imageSlideContent,
        };
      }
    }

    const videoSlideContent = slide.querySelector('video');
    if (videoSlideContent) {
      return {
        type: 'video',
        element: videoSlideContent,
      };
    }
  };

  // Add the wanted lazyload class if it isn't already there
  // Current options: lazyload | swa-lazyload
  const addSlideLazy = (element, lazyclass) => {
    if (
      !element ||
      element.classList.contains('lazyload') ||
      element.classList.contains('lazyloaded') ||
      element.classList.contains('swa-lazyload') ||
      element.classList.contains('swa-lazyloaded') ||
      (element.closest(".swa-homepage__component__top-teaser") && element.parentElement.parentElement.dataset.position === "1") // removed lazyload for the first slide of top teaser carousel
    ) {
      return;
    }
    element.classList.add(lazyclass);
  };

  if (!isAutoPlayInit && carouselInstance && carouselInstance.settings) {
    carouselAutoplayValue = carouselInstance.settings.autoplay;
    isAutoPlayInit = true;
  } else if (!carouselInstance) {
    carouselAutoplayValue = 0;
    isAutoPlayInit = true;
  }

  const carouselElementArrows = carouselElement.querySelector(
    '.glide__arrows, .splide__arrows'
  );

  const carouselBulletsContainer = carouselElement.querySelector(
    '.glide__bullets, .splide__pagination'
  );

  let carouselElementBullets = null;

  if (carouselBulletsContainer) {
    carouselElementBullets = carouselBulletsContainer.querySelectorAll(
      ':not(.glide__bullet--active):not(.is-active)'
    );
  }

  const setAutoplayValue = () => {
    if (isSplide) {
      carouselInstance.on('moved', () => {
        if (carouselInstance.options.autoplay !== 0) {
          carouselInstance.Components.Autoplay.play();
        }
      });
    } else {
      carouselInstance.settings.autoplay = carouselAutoplayValue;
    }
  };

  if (carouselElementArrows) {
    // Reset the autoplay when user clicks on arrows
    carouselElementArrows.addEventListener('click', setAutoplayValue);
  }

  if (carouselElementBullets) {
    // Reset the autoplay when user clicks on bullets
    carouselElementBullets.forEach(bullet => {
      bullet.addEventListener('click', setAutoplayValue);
    });
  }

  // Play the video from within the carousel slide
  const videoPlay = (element, carousel) => {
    if (!element) {
      return;
    }

    // If we don't have a carousel, we shouldn't try to move to the next slide so we simply loop the video
    if (!carousel) {
      element.loop = true;
      element.play();
      return;
    }

    // If we don't have autoplay on the carousel, once again, we shouldn't move to the next slide
    if (isSplide) {
      if (carousel.options && carousel.options.autoplay === 0 && element.loop === false) {
        element.loop = true;
        element.play();
        return;
      }
    } else {
      if (carousel.settings.autoplay === 0 && element.loop === false) {
        element.loop = true;
        element.play();
        return;
      }
    }

    // If we do have a carousel, we play the video and when it reaches the end, we move to the next slide
    if (isSplide) {
      if (carousel.Components) {
        carousel.Components.Autoplay.pause();
      }
    } else {
      carousel.pause();
      carousel.settings.autoplay = 0; // Autoplay set to 0 when the video is played
    }
    element.play();

    element.onended = () => {
      carousel.go('>');
      if (isSplide) {
        if (carousel.Components) {
          carousel.Components.Autoplay.play(); // This will reset the autoplay when the video ended
        }
      } else {
        carousel.settings.autoplay = carouselAutoplayValue; // This will reset the autoplay when the video ended
      }
      element.currentTime = 0;
    };
  };

  // Pause the video element from within the carousel slide
  const videoPause = element => {
    if (!element) {
      return;
    }

    if (element.duration === element.currentTime) {
      element.currentTime = 0;
    }

    element.pause();
  };

  // Wait for the video to load, in order for it to be playable
  // When the video is ready to play, lazyload the next slide and play the video
  const videoLoader = (element, slide, carousel) => {
    if (!element) {
      return;
    }

    let carouselIsPaused = false;
    const videoInterval = setInterval(() => {
      const videoPoster = slide.querySelector('img[data-poster="true"]');

      if (element.readyState >= 3) {
        if (carousel) {
          carouselIsPaused = false;
          nextSlideLoader(slide, carousel);
        }

        if (videoPoster) {
          videoPoster.parentNode.removeChild(videoPoster);
        }

        videoPlay(element, carousel);
        clearInterval(videoInterval);
      } else if (carousel && !carouselIsPaused) {
        carouselIsPaused = true;
        if (isSplide) {
          carousel.options = { autoplay: 0 };
        } else {
          carousel.pause();
        }
      }
      if (detectSafari()) {
        const videoIos = slide.querySelector('video');

        if (videoIos && videoPoster && videoPoster.parentNode !== null) {
          videoPoster.src = videoPoster.parentNode.querySelector('source').src;
        }

        clearInterval(videoInterval);
      } else if (carousel && !carouselIsPaused) {
        carouselIsPaused = true;
        if (isSplide) {
          carousel.options = { autoplay: 0 };
        } else {
          carousel.pause();
        }
      }
    }, 250);
  };

  // Wait for the image to load, in order for it to be displayed
  // When the image is ready, lazyload the next slide
  const imageLoader = (element, slide, carousel) => {
    if (!element) {
      return;
    }

    let carouselIsPaused = false;
    const imageInterval = setInterval(() => {
      if (element.complete && !element.dataset.hasLoaded) {
        element.setAttribute('data-has-loaded', true);

        if (carousel) {
          nextSlideLoader(slide, carousel);

          // In case of skipping a slide, ending up on another which has not yet loaded,
          // pause autoplay and resume it only after the image loaded and the autoplay time has passed
          if (carousel.settings && carousel.settings.autoplay) {
            setTimeout(() => {
              if (
                (slide.classList.contains('glide__slide--active') ||
                (slide.classList.contains('swa-splide-teaser-carousel__item') && slide.classList.contains('is-visible'))) &&
                carouselIsPaused
              ) {
                carousel.go('>');
                carouselIsPaused = false;
              }
            }, carousel.settings.autoplay);
          }
        }
        clearInterval(imageInterval);
      } else if (carousel && !carouselIsPaused && !element.dataset.hasLoaded) {
        carouselIsPaused = true;
        if (isSplide) {
          carousel.options = { autoplay: 0 };
        } else {
          carousel.pause();
        }
      }
    }, 250);
  };

  // Repeat the logic from the active slide by lazyloading what's coming next
  const nextSlideLoader = (slide, carousel) => {
    if (!slide) {
      return;
    }

    const nextSlide = slide.nextElementSibling;

    if (!nextSlide) {
      return;
    }

    const nextSlideContent = validateSlideContent(nextSlide);

    if (
      !nextSlideContent ||
      !nextSlideContent.element ||
      nextSlideContent.element.classList.contains('lazyloaded') ||
      nextSlideContent.element.classList.contains('swa-lazyloaded')
    ) {
      return;
    }

    // When reaching the end of the carousel we have to update its clones
    // in order to avoid replacement of old clones (which are not lazyloaded) into the real slides
    if (carousel) {
      if (nextSlide.classList.contains('glide__slide--clone')) {
        carousel.update();
      }
      else if (nextSlide.classList.contains('splide__slide--clone')) {
        carousel.options = {};
      }
      return;
    }

    switch (nextSlideContent.type) {
    case 'image':
      addSlideLazy(nextSlideContent.element, 'lazyload');
      break;
    case 'video':
      addSlideLazy(nextSlideContent.element, 'swa-lazyload');
      customLazyload(nextSlideContent.element);
      break;
    }
  };

  // Get the carousel's active slide and its target element (image or video)
  const activeSlide = getActiveCarouselSlide(carouselElement);
  const activeSlideContent = validateSlideContent(activeSlide);

  if (!activeSlide || !activeSlideContent || !activeSlideContent.element) {
    return;
  }

  // Based on the active slide target element's type, lazyload its content
  switch (activeSlideContent.type) {
  case 'image':
    if (onRun) {
      addSlideLazy(activeSlideContent.element, 'lazyload');
      imageLoader(activeSlideContent.element, activeSlide, carouselInstance);
    }
    break;
  case 'video':
    if (onRun) {
      addSlideLazy(activeSlideContent.element, 'swa-lazyload');
      customLazyload(activeSlideContent.element);
      videoLoader(activeSlideContent.element, activeSlide, carouselInstance);
      return;
    }
    videoPause(activeSlideContent.element);
    break;
  }

};


// ------ TAB NAVIGATION
export const setTabIndex = (elements, tabIndex) => {
  if (
    !elements ||
    !elements.length ||
    tabIndex === undefined ||
    (tabIndex !== -1 && tabIndex !== 0)
  ) {
    return;
  }

  elements.forEach(element => {
    element.tabIndex = tabIndex;
  });
};

export const ignoreContentOnTab = (
  type,
  carouselElement,
  carouselSlidesElement
) => {
  if (!type) {
    return;
  }

  const selectTabbableContent = (element, tabIndex) => {
    const anchors = element.querySelectorAll('a');
    const buttons = element.querySelectorAll('button');

    if (anchors) {
      setTabIndex(anchors, tabIndex);
    } else if (buttons) {
      setTabIndex(buttons, tabIndex);
    }
  };

  switch (type) {
  case 'init':
    selectTabbableContent(carouselSlidesElement, -1);
    selectTabbableContent(getActiveCarouselSlide(carouselElement), 0);
    break;
  case 'beforeChange':
    selectTabbableContent(getActiveCarouselSlide(carouselElement), -1);
    break;
  case 'afterChange':
    selectTabbableContent(getActiveCarouselSlide(carouselElement), 0);
    break;
  }
};

// Adds the outline needed for the keyboard navigation
// Works with TAB, ENTER and LEFT/RIGHT ARROW keys
export const renderOutline = e => {
  const key = e.which || e.keyCode;

  if (key === 9 || key === 13 || key === 37 || key === 39) {
    // Add an event listener so we can remove
    // the outline generated by keyboard navigation, if using the mouse again
    if (currentTabElement === '') {
      window.addEventListener('click', resetOutlineOnClick);
    }

    previousTabElement = currentTabElement;
    currentTabElement = document.activeElement;

    // Add the outline to the focused element and remove the previous one
    if (key !== 13) {
      currentTabElement.classList.add(outlineClass);
      resetOutline(previousTabElement);
    } else {
      const elementType = currentTabElement.nodeName;
      // Remove the outline if the focused element is an input or
      // if the focus has changed from the element when the enter key has been pressed
      if (elementType === 'INPUT' || previousTabElement !== currentTabElement) {
        resetOutline(previousTabElement);
      }
    }
  }
};

const resetOutlineOnClick = e => {
  // e.detail checks if the event is actually a click event
  // since the window click listener also gets triggered by the enter key
  if (e && e.detail === 0) {
    return;
  }

  // If the mouse has been used, the listener makes no sense
  // until the keyboard navigation is used again, therefore we can remove it
  window.removeEventListener('click', resetOutlineOnClick);
  resetOutline(currentTabElement);
  currentTabElement = '';
  resetOutline(previousTabElement);
  previousTabElement = '';
};

// Removes the outline for the given element
const resetOutline = element => {
  if (element.classList) {
    element.classList.remove(outlineClass);
  }
};

// ------ DETECTS
export const detectMobile = () => {
  const width = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0
  );

  return width < viewPorts.desktop;
};

export const detectTablet = () => {
  const width = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0
  );

  return width < viewPorts.desktop && width > viewPorts.tablet;
};

export const detectDesktop = () => {
  const width = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0
  );

  return width >= viewPorts.desktop && width < viewPorts.wide;
}

export const detectMobileUntilTablet = () => {
  const width = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0
  );

  return width < viewPorts.tablet;
};

export const detectWideScreen = () => {
  const width = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0
  );

  return width > viewPorts.wide;
};

export const detectIE = () => {
  return !!window.MSInputMethodContext && !!document.documentMode;
};

export const detectEdge = () => {
  if (
    navigator.userAgent.includes('Edge') ||
    navigator.userAgent.includes('Trident')
  ) {
    return true;
  }
};

export const detectSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  let isSafari = false;
  try {
    isSafari =
      /constructor/i.test(window.HTMLElement) ||
      (function (p) {
        return p.toString() === '[object SafariRemoteNotification]';
      })(!window['safari'] || safari.pushNotification);
  } catch (err) {}
  isSafari =
    isSafari ||
    (ua.indexOf('safari') != -1 &&
      (!(ua.indexOf('chrome') != -1) && ua.indexOf('version/') != -1));
  return isSafari;
};

const detectOS = () => {
  var curOS = 'unknown';
  if (navigator.appVersion.indexOf('Win') != -1) curOS = 'windows';
  if (navigator.appVersion.indexOf('Mac') != -1) curOS = 'macos';
  return curOS;
};

export const applyOsOverwrite = () => {
  const currentOS = detectOS();
  if (currentOS !== 'unknown') {
    const body = document.querySelector('body');
    body && body.classList.add(`swa-is-${currentOS}`);
  }
};

export const horizontalScroll = (e, targetElement) => {
  e.preventDefault();

  if (e.type !== 'wheel') {
    return;
  }

  let delta = (e.deltaY || -e.wheelDelta || e.detail) >> 10 || 1;
  delta = delta * -300;
  targetElement.scrollLeft -= delta;
};

export const focusSearchOnDialogClose = (e, dialog) => {
  if (!dialog) {
    return;
  }

  dialog.hide();

  if (e.target.type === 'search') {
    e.target.focus();
  }
};

export const toggleDialogState = (dialogId, dialogIsOpen) => {
  const currentDialog = document.getElementById(dialogId);
  if (!currentDialog) {
    return;
  }

  const body = document.getElementsByTagName('body')[0];
  const header = document.querySelector('header') || document.querySelector('.js-checkout-header');
  const footer = document.querySelector('footer');
  const main = document.querySelector('.js-swa-page-main section');

  const isHeaderSticky = header.classList.contains('swa-header--sticky');
  const isDialogFull = currentDialog.querySelector('.swa-dialog--full');
  const isSortingDialog = currentDialog.classList.contains('swa-sorting__dialog');
  const dialogCellParent = currentDialog.closest('.swa-grid__cell');
  const isCenteredDialog = currentDialog.querySelector('.swa-dialog--center')

  // In case we have a dialog in a cell, we have to increase the z-index of the cell, otherwise we increase it for the dialog itself
  const classToggleTarget = dialogCellParent ? dialogCellParent : currentDialog;

  const styleClasses = {
    open: 'swa-dialog-open',
    closed: 'swa-dialog-closed',

    hasOpen: 'swa-has-dialog-open',
    full: 'swa-has-dialog-open--full',
    absolute: 'swa-has-dialog-open-absolute',
    absoluteIE: 'swa-has-dialog-open-absolute-ie',

    stickyHeader: 'swa-header--sticky',
  };

  header && header.setAttribute('aria-hidden', dialogIsOpen);
  footer && footer.setAttribute('aria-hidden', dialogIsOpen);
  main &&
    [...main.children].forEach(section => {
      const children = section.querySelector(`#${dialogId}`);
      if (!children) {
        section.setAttribute('aria-hidden', dialogIsOpen);
      }
    });

  const openDialogClassToggle = (target, state) => {
    // IE case - add the absolute class in general or the absoluteIE for the content stage carousel dialog (absoluteIE does not have a top property)
    // Edge case - add the absolute class
    // Safari Content Stage Carousel - We want to remove the stickyness of the header without any extra class

    if (isDialogFull) {
      if (detectEdge()) {
        target.classList.toggle(styleClasses.absolute, state);
      }

      if (!isCenteredDialog) {
        target.classList.remove(styleClasses.hasOpen);
        target.classList.toggle(styleClasses.full, state);
      }
    } else {
      if (!isCenteredDialog) {
      target.classList.remove(styleClasses.full);
      target.classList.toggle(styleClasses.hasOpen, state);
      }
    }
  };

  if (dialogIsOpen) {
    // If the page is already scrolled, preserve the scrollPosition value before applying the
    // "no scroll" CSS class
    const scrollPosition = window.pageYOffset;

    // add the scrollPosition value to data-scrolltop
    if(scrollPosition) {
      body.setAttribute('data-scrolltop', String(scrollPosition));
      body.setAttribute('style', `top: -${scrollPosition}px`);
    }

    openDialogClassToggle(classToggleTarget, true);

    currentDialog.classList.remove(styleClasses.closed);
    currentDialog.classList.add(styleClasses.open);

    // Responsive repaint for Cloudinary so the images show up
    if (window.cl !== undefined) {
      window.cl.responsive();
    }
  } else {
    /*
     * fix for iOS, styleClasses.noScroll may return a different class than the
     * one previously added to the body, causing the page to remain un-scrollable,
     * thus we have have to make sure any of the classes is removed
     *
     * There are cases where both of them are added to the body
     */
    body.classList.remove('swa-no-scroll');
    body.classList.remove('swa-no-scroll-with-scrollbar');

    // If the page was previously scrolled, restore the "old" scrollPosition value
    const scrollPosition = body.dataset.scrolltop || 0;

    body.removeAttribute('style');
    window.scrollTo(0, scrollPosition);

    //Remove z-index only after the CSS transition finished
    currentDialog.classList.add(styleClasses.closed);
    currentDialog.classList.remove(styleClasses.open);

    openDialogClassToggle(classToggleTarget, false);
  }
};

export const dispatchCustomEvent = (customEvent, data) => {
  document.dispatchEvent(new CustomEvent(customEvent, { detail: data }));
};

export const setDialogOffset = (dialogElement, dialogTriggerContainer) => {
  const header = document.getElementById('header');
  const dialogOffsetReferenceElement = document.querySelector(
    `.${dialogTriggerContainer}`
  );

  if (!header || !dialogElement.children) {
    return;
  }

  const hasStickyHeader = document.body.classList.contains('swa-header--sticky');
  const clientHeight = window.innerHeight;
  const headerHeight = header.offsetHeight;

  const referenceElementTop = dialogOffsetReferenceElement ? dialogOffsetReferenceElement.getBoundingClientRect().top : 0;
  let top = hasStickyHeader ? headerHeight : referenceElementTop;

  let maxHeight = hasStickyHeader ?
    clientHeight - headerHeight :
    clientHeight - referenceElementTop;

  if (detectMobile()) {
    top = 0;
    maxHeight = clientHeight;
  }

  dialogElement.children.forEach(element => {
    element.setAttribute('style', `top: ${top}px; max-height: ${maxHeight}px`);
  });
};

export const positionOverlay = (underneath, overlay) => {
  const SPACING_OFFSET_CONST = 1;
  const positionReference = underneath.getBoundingClientRect();
  overlay.setAttribute(
    'style',
    `margin-top: ${positionReference.top +
      positionReference.height +
      SPACING_OFFSET_CONST}px`
  );
};

export const preventDefaultClick = target => {
  let element;
  // Check whether the target is a simple string selector of the element or the element itself
  if (typeof target === 'object' && target.nodeType === 1) {
    element = target;
  } else {
    element = document.querySelector(`.${target}`);
  }

  if (!element) {
    return;
  }

  element.addEventListener('click', e => e.preventDefault());
};

export const ignoreCarouselClones = carouselSelector => {
  const cloneSlides = document.querySelectorAll(
    `${carouselSelector} .glide__slide--clone, ${carouselSelector} .splide__slide--clone`
  );

  if (!cloneSlides) {
    return;
  }

  cloneSlides.forEach(element => {
    element.tabIndex = -1;
    [...element.children].forEach(child => {
      child.tabIndex = -1;
    });
  });
};

export const getDomNodesNumber = () => {
  const maximumNodes = 1500;
  const totalNodes = document.getElementsByTagName('*').length;
  const headerNodes =
    document.querySelector('header').getElementsByTagName('*').length + 1;

  const footerNodes =
    document.querySelector('footer').getElementsByTagName('*').length + 1;

  const percentage = ((totalNodes / maximumNodes) * 100).toFixed(2);

  console.log(`
  ---------------------------------------------------------------
    There are ${totalNodes} DOM nodes on the current page.
    Status: ${percentage}% in use, ${100 -
    percentage}% left until reaching the limit.

    === Header nodes: ${headerNodes}.
    === Footer nodes: ${footerNodes}.
    === DEV nodes: ${totalNodes - headerNodes - footerNodes}.
  ---------------------------------------------------------------
  `);
};

export const debounceEvent = (callback, wait) => {
  let timeout = null;
  return (...args) => {
    const next = () => callback(...args);
    clearTimeout(timeout);
    timeout = setTimeout(next, wait);
  };
};

export const getArrowKeyDirection = keyCode => {
  return {
    37: 'left',
    39: 'right',
    38: 'up',
    40: 'down',
  } [keyCode];
};

export const isArrowKey = keyCode => {
  return !!getArrowKeyDirection(keyCode);
};

export const showHideSkipLinks = e => {
  const accessBox = document.getElementById('js-accesskey-box');
  const isPatternlab = document.querySelector('.js-patternlab-page');
  const key = e.which || e.keyCode;

  if (key === 13) {
    accessBox.classList.add('sr-only');
    return;
  }

  if (!accessBox && !isPatternlab) {
    return;
  }

  if (
    document.activeElement.classList.contains('accesskey-box__link') ||
    document.activeElement.classList.contains('accesskey-box__unordered-list')
  ) {
    accessBox.classList.remove('sr-only');
  } else {
    accessBox.classList.add('sr-only');
  }
};

export const calculateContainerHeight = (element, scale) => {
  if (!element || !scale) {
    return;
  }

  // We have to calculate the height on mobile so the content fits on the initial screen.
  // In CSS, 100vh means the whole height of the screen, including the address bar and we don't want that.
  if (scale === 'mobile' && detectMobile()) {
    element.style.height = `calc(${window.innerHeight}px - 7rem)`;

    // Adding a listener, so the height is being recalculated at every orientation change of the phone
    const matchMedia = window.matchMedia('(orientation: portrait)');
    matchMedia.addListener(() => {
      element.style.height = `calc(${window.innerHeight}px - 7rem)`;
    });
  }
};

export const calculateLandscapeContainerHeightMultipleCTA = (element, scale, calculatedMaxHeight = 0) => {
  if (!element || !scale) {
    return;
  }

  // provides space to top and bottom of content (not to be exact and sticky to slide top/bottom border)
  const staticAddedHeightInLandscape = '10rem';

  if (scale === 'mobile' && detectMobile()) {

    if (window.matchMedia('(orientation: portrait)').matches) {
      element.style.height = `calc(${window.innerHeight}px - 7rem)`;
    } else {
      if (calculatedMaxHeight && window.matchMedia('(orientation: landscape)').matches) {
        element.style.height = `calc(${calculatedMaxHeight}px + ${staticAddedHeightInLandscape})`;
      } else {
        element.style.height = `calc(${window.innerHeight}px - 7rem)`;
      }
    }
  }
};

export const calculateHighestCTASlide = (CTASlides) => {

  if (!CTASlides) {
    return;
  }

  let CTASlidesHeightArray = [];

  CTASlides.forEach(slide => {

    let slideHeight = 0;

    // looping through children elements in slide
    slide.children.forEach(childElement => {
      slideHeight += childElement.offsetHeight;

      // Adding possible margins dynamically
      let style = getComputedStyle(childElement);
      slideHeight += parseInt(style.marginTop);
      slideHeight += parseInt(style.marginBottom);
    });

    CTASlidesHeightArray.push(slideHeight);
  });

  return Math.max(...CTASlidesHeightArray);
};

// This functions is not used anymore since the header was redesigned
// In case someone will use it in the future, it is adapted
export const observeStickyHeader = () => {
  const MutationObserver =
    window.MutationObserver ||
    window.WebKitMutationObserver ||
    window.MozMutationObserver;
  const header = document.querySelector('header');
  const body = document.querySelector('body');
  const config = {
    attributes: true,
    attributeFilter: ['class'],
    childList: false,
    characterData: false,
    subtree: false,
  };

  const toggleStickyHeaderClass = (headerClassList, body) => {
    if (headerClassList.includes('swa-header--sticky')) {
      body.classList.add('swa-has-sticky-header');
    } else {
      body.classList.remove('swa-has-sticky-header');
    }
  };

  if (!header) {
    return;
  }

  const headerClassList = Array.from(header.classList);

  toggleStickyHeaderClass(headerClassList, body);

  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class'
      ) {
        toggleStickyHeaderClass(Array.from(header.classList), body);
      }
    });
  });

  observer.observe(header, config);
};

//displays all Media Queries used in a CSS string sent as a parameter
export const extractMediaQueries = (inputString = '') => {
  //matches '@media (){' or '@media () and (){'
  const regex = /@media\s*(\([^)]*\))\s*(and\s*(\([^)]*\))\s*)?{/gim;

  try {
    const matches = [...inputString.match(regex)];
    const uniqueMatches = new Set(matches);
    console.table(Array.from(uniqueMatches));
  } catch (err) {
    console.log(
      'Invalid CSS input string, it does not contain any media queries'
    );
  }
};

// TODO: This is used for omnichannel - webkitLineClamp and detectIE is used for
//  storefinder and checkout step 3 (on the C&R layer)
window.webkitLineClamp = webkitLineClamp ? webkitLineClamp : null;
window.detectIE = detectIE ? detectIE : null;


// Dummy functions needed to simulate actions in PL
export const dummyAddToBasket = e => {
  const addToBasketBtn = e.target.closest('.swa-button');
  const btnText = addToBasketBtn.querySelector('.swa-button__text');
  const globalMessagesContainer = document.getElementById(
    'swaGlobalMessagesContainer'
  );

  const addToBasketLoader = addToBasketBtn.querySelector('.swa-loader');

  addToBasketBtn.classList.add('swa-button--disabled');
  addToBasketBtn.classList.add('swa-button--loading');
  addToBasketBtn.setAttribute('disabled', 'disabled');

  if (addToBasketLoader) {
    addToBasketLoader.classList.remove('swa-hidden');
  }

  setTimeout(() => {
    if (btnText) {
      btnText.textContent = addToBasketBtn.dataset.another;
    }

    addToBasketBtn.classList.remove('swa-button--loading');
    addToBasketBtn.classList.remove('swa-button--disabled');
    addToBasketBtn.removeAttribute('disabled');
    if (addToBasketLoader) {
      addToBasketLoader.classList.add('swa-hidden');
    }

    if (globalMessagesContainer) {
      globalMessagesContainer.innerText =
        addToBasketBtn.dataset.actionAlert || 'One item added to basket';
      globalMessagesContainer.classList.remove('swa-slide-up');
      globalMessagesContainer.classList.add('swa-slide-down');
    }

    setTimeout(() => {
      if (globalMessagesContainer) {
        globalMessagesContainer.classList.remove('swa-slide-down');
        globalMessagesContainer.classList.add('swa-slide-up');
      }
    }, 1500); //global message display duration

    const basketStatusElement = document.getElementById('basket-status');
    if (!basketStatusElement) {
      return;
    }

    basketStatusElement.textContent = addToBasketBtn.dataset.actionAlert;
  }, 2000); //Button loading state duration - just for PL, not needed in Hybris
};

/*
* Helper to calculate vh manually.(for browsers which do not support vh measure for height calculation)
* Add fallback in scss.
* Ex: max-height: calc(100vh - 16.4rem);//used as fallback in case the browser does not support custom properties
*     max-height: calc(var(--vh, 1vh) * 100 - 16.4rem);
*/
export  const setHeightToVh = () => {
  let vh = window.innerHeight * 0.01 + 'px'; //get the value of 1vh;
  document.documentElement.style.setProperty('--vh', vh); //set the value in the --vh newly created property, at the root of the document
};

// Helper for calculating the padding needed for top teasers (depending on the selector, currently the options are PLP Top Teaser or Account Top Teaser)
// when the header is transparent and its height is changing (height varies depending on the existence of Top Banner,Accessibility Banner or CmsCountdown)
export const addPaddingOnTeaserContent = (teaserContent, additionalSlot) => {
  const header = document.querySelector('.js-swa-header');

  if (teaserContent && !header.classList.contains('swa-header--solid-border')) {
    const plpTopTeaserNew = document.querySelector('.swa-plp-top-teaser--new');//new modifier class for plp top teaser wrapper, which will replace the wrapper one with the cleanup ticket
    const plpTopTeaserNewImg = document.querySelector('.swa-plp-top-teaser--new .js-swa-plp-top-teaser-image-wrapper');

    if (!plpTopTeaserNew || (plpTopTeaserNew && !detectMobile())) { //calculate teaser content padding only for desktop if the new modifier class is present
      let teaserContentPaddingTop;
      if (detectMobile()) {
        teaserContentPaddingTop = 0;
      } else {
        teaserContentPaddingTop = 26;
      }
      teaserContent.setAttribute('style', 'padding-top: ' + (header.getBoundingClientRect().height + teaserContentPaddingTop) + 'px');

      // if top banner/countdown are placed inside header - TODO: remove this with cleanup up ticket for PLP top teaser redesign
      if (plpTopTeaserNewImg && additionalSlot) {
        plpTopTeaserNewImg.setAttribute('style', 'margin-top: 0px');
      }
    } else if (plpTopTeaserNewImg && detectMobile()) {
      teaserContent.setAttribute('style', 'padding-top: 0px'); // set padding 0 when modifier class and mobile/tablet

      // if top banner/countdown are placed inside header - TODO: remove this with cleanup up ticket for PLP top teaser redesign
      if (additionalSlot) { // if also top banner/countdown are present, margin-top needs to be calculated for teaser image, in order not to cover it on mobile
        plpTopTeaserNewImg.setAttribute('style', 'margin-top: ' + additionalSlot.getBoundingClientRect().height + 'px');
      }
    }
  }
};

//helper to be used to handle all video interactions in all cms components
export const handleCmsVideoControls = (elem) => {
  const video = elem.querySelector('video'),
    play = elem.querySelector('.swa-cms-video__control--play'),
    pause = elem.querySelector('.swa-cms-video__control--pause'),
    mute = elem.querySelector('.swa-cms-video__control--mute'),
    unmute = elem.querySelector('.swa-cms-video__control--unmute');

  if (mute) {
    mute.addEventListener('click', () => {
      video.muted = false;

      mute.classList.add('swa-hidden');
      unmute.classList.remove('swa-hidden');
    })
  }

  if (unmute) {
    unmute.addEventListener('click', () => {
      video.muted = true;

      unmute.classList.add('swa-hidden');
      mute.classList.remove('swa-hidden');
    })
  }

  if (play) {
    play.addEventListener('click', () => {
      video.play();

      play.classList.add('swa-hidden');
      pause.classList.remove('swa-hidden');
    })
  }

  if (pause) {
    pause.addEventListener('click', () => {
      video.pause();

      pause.classList.add('swa-hidden');
      play.classList.remove('swa-hidden');
    })
  }

  if(video) {
    video.addEventListener('ended', () => {
      if (pause) {
        pause.classList.add('swa-hidden');
        play.classList.remove('swa-hidden');
      }
    })
  }
};

/* CMS video intersection behaviour
 * 1. We have two main types: carousel or simple element (const isCarousel)
 * 2. The carousel
 *  - can or can not contain video elements (const hasVideo)
 *  - if a video element is present, it may have one or more data-src attributes present (multiplesrc data attribute)
 * 3. Simple element
 *  -  it may be the case where multiple data-src atributes are present (multiplesrc data attribute)
 */
export const handleCMSVideoIntersectionBehaviour = (entry) => {
  const isCarousel = entry.target.classList.contains('swa-fullscreen-product-carousel__image-carousel');
  let paramsObj = {};

  //determine the current viewport
  const getViewport = () => {
    let viewport = '';
    let posterImg = '';

    switch (true) {
      case detectMobileUntilTablet():
        viewport = 'srcMobile';
        posterImg = "posterMobile";

        break;
      case detectTablet():
        viewport = 'srcMobile';
        posterImg = "posterMobile";

        break;
      case detectDesktop():
        viewport = 'srcDesktop';
        posterImg = "posterDesktop";

        break;
      case detectWideScreen():
        viewport = 'srcDesktop';
        posterImg = "posterDesktop";

        break;
    }

    return { viewport, posterImg };
  };

  //set the proper src
  const setSrc = (videoCollection) => {
    const { viewport, posterImg } = getViewport();

    videoCollection.forEach(video => {
      const src = video.dataset.multiplesrc ? video.dataset[viewport] : video.dataset.src;
      const poster = video.dataset.multiplesrc ? video.dataset[posterImg] : video.dataset.poster;

      if (poster) {
        video.setAttribute('poster', poster);
      }
      video.setAttribute('src', src);
    });
  };

  //handle carousel video behaviour
  const handleCarouselVideoBehaviour = (params) => {
    const { hasVideo, activeSlideHasVideo, videoCollection } = params;

    if (hasVideo) {
      if (activeSlideHasVideo && entry.isIntersecting) {
        const video = entry.target.querySelector('.is-active').querySelector('video');
        const parent = video.closest('.swa-fullscreen-product-carousel__slide');
        const firstSlidePlay = parent.querySelector('.swa-cms-video__control--play');
        const firstSlidePause = parent.querySelector('.swa-cms-video__control--pause');

        video.play();
        firstSlidePlay.classList.add('swa-hidden');
        firstSlidePause.classList.remove('swa-hidden');
      }

      videoCollection.forEach(video => {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach(mutation => {
            const video = mutation.target.querySelector('video');
            const play = mutation.target.querySelector('.swa-cms-video__control--play');
            const pause = mutation.target.querySelector('.swa-cms-video__control--pause');

            if (mutation.target.classList.contains('is-visible') && video.dataset.autoplay && entry.isIntersecting) {
              video.play();
              play.classList.add('swa-hidden');
              pause.classList.remove('swa-hidden');
            } else {
              video.pause();
              play.classList.remove('swa-hidden');
              pause.classList.add('swa-hidden');
            }
          });
        });

        observer.observe(video.closest('.swa-fullscreen-product-carousel__slide'), {
          attributes: true,
          attributeFilter: ['class'],
          childList: false,
          characterData: false
        });
      });
    }
  };

  //handle simple cms video
  const handleSimpleElementVideoBehaviour = (params) => {
    const { hasVideo, videoCollection, play, pause } = params;

    if (hasVideo) {
      const video = videoCollection[0];

      if (video.dataset.autoplay && entry.isIntersecting) {

        //video.play() returns a Promise, which will start loading the video asynchronously;
        var videoWillPlayPromise = video.play();

        if (videoWillPlayPromise !== undefined) {
          // in case video.pause() is executed too early (user scrolls to fast and the video does not intersect the view) and video.play() loading is interrupted,
          // handle the videoWillPlayPromise response with .then() method, making sure the code inside will not execute until the media is playing.
          videoWillPlayPromise.then(() => {
            // video started playing, we can show the playing UI
            play.classList.add('swa-hidden');
            pause.classList.remove('swa-hidden');
          })
            .catch(error => {
              // catch the videoWillPlayPromise rejection, prevent the video autoplay, and add the paused UI.
              pause.classList.add('swa-hidden');
              play.classList.remove('swa-hidden');
            });
        }
      } else if (!entry.isIntersecting) {
        setTimeout(() => {
          video.pause();
        }, 0);
        pause.classList.add('swa-hidden');
        play.classList.remove('swa-hidden');
      }
    }
  };

  setTimeout(() => {//in case we have a carousel wait for splide to add is-active class
    if (isCarousel) {
      if (entry.target.querySelector('.is-active')) {
        paramsObj.hasVideo =  entry.target.querySelector('video') ? true : false;
        paramsObj.activeSlideHasVideo = entry.target.querySelector('.is-active').querySelector('video') ? true : false;
        paramsObj.videoCollection = paramsObj.hasVideo ? entry.target.querySelectorAll('video') : '';

        if (paramsObj.hasVideo) {
          setSrc(paramsObj.videoCollection);
        }
        handleCarouselVideoBehaviour(paramsObj);
      }
    } else {
      paramsObj.hasVideo = entry.target.querySelector('video') ? true : false;
      paramsObj.videoCollection = paramsObj.hasVideo ? new Array(entry.target.querySelector('video')) : '';
      paramsObj.play = paramsObj.hasVideo ? entry.target.querySelector('.swa-cms-video__control--play') : '';
      paramsObj.pause = paramsObj.hasVideo ? entry.target.querySelector('.swa-cms-video__control--pause') : '';

      if (paramsObj.hasVideo && entry.isIntersecting) {
        setSrc(paramsObj.videoCollection);
      }
      handleSimpleElementVideoBehaviour(paramsObj);
    }
  }, 0);
};

// Removing element listeners by deep cloning element
export const removeElementListeners = (elem) => {
  let clonedElem = elem.cloneNode(true);
  elem.parentNode.replaceChild(clonedElem, elem);
}

/* Helper function to load an external script on demand
 * @params: url, async, customEvent
 * url: url to be set for the script src
 * async: true/false
 * customEvent: custom event to be dispatched when the download has finished
 *
 * Logic:
 * - create script tag
 * - set attributes
 * - append script tag
 * - log success/error events
 * - if success, dispatch custom event
 */
export const loadExternalScript = (url, async = true, customEvent) => {
  let scriptElem = document.createElement("script");

  scriptElem.setAttribute("src", url);
  scriptElem.setAttribute("type", "text/javascript");
  scriptElem.setAttribute("async", async);

  document.body.appendChild(scriptElem);

  // success event
  scriptElem.addEventListener("load", () => {
    dispatchCustomEvent(customEvent);
    console.log("File loaded")
  });
  // error event
  scriptElem.addEventListener("error", (ev) => {
    console.log("Error on loading file", ev);
  });
};

/* Helper function to determine if a script tag is or isn't in the page
 * @params: str
 * str: a string that reflects the url set on the script that we want to check for
 *
 * Logic:
 * - create an HTML collection containing all the script tags in the page
 * - iterate through the collection and compare the script tags src's with the verification str param
 * - return true if a match is found, false if not
 */
export const checkIfScriptExists = (str) => {
  const scriptColection = document.getElementsByTagName('script');;
  let scriptInPage = false;

  for (const script of scriptColection) {
    if (script.src === str) {
      scriptInPage = true;
    }
  }
  return scriptInPage;
};

/* If we detect a mobile/tablet add an event listener, and if an "enter" is detected, blur the input and close the virtual keyboard */
export const mobileKeyboardBlur = (input) => {
  const notDesktop = detectMobile() || detectTablet();

  if (notDesktop) {
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        input.blur();
      }
    })
  }
};
